import { computed, Injectable, InjectionToken, signal } from '@angular/core';
import { ValueConverter } from '@skylab-shared/core';
import { isUndefined } from '@skylab-shared/utils';
import { injectTranslation } from '@skylab-angular-shared/core';
import { Chip, compareChipsValues } from '../filter-chips';

export interface FilterChipCollectionItem {
	label: string;
	value: unknown;
	displayLabel?: string;
}

export interface FilterChipStrategy {
	setInitialValue(): void;
	resetDefaultValue(): void;
	onValueChange(value: unknown): void;
	refresh(): void;
	readonly chosenValues: unknown[];
}

export const FILTER_CHIP_STRATEGY = new InjectionToken<FilterChipStrategy>('FilterChipStrategy');

@Injectable()
export class FilterChipContext {
	public readonly chip = signal<Chip | undefined>(undefined);
	public readonly value = signal<unknown>(undefined);
	public readonly collection = signal<FilterChipCollectionItem[]>([]);

	public readonly _T = injectTranslation();

	public readonly hasValue = computed(() => {
		const value = this.value();
		const chip = this.chip();
		if (!chip) return false;

		const defaultValue = !isUndefined(chip.defaultValue) ? chip.defaultValue : chip.initialChipValue;
		return compareChipsValues(value, defaultValue);
	});

	public converter!: ValueConverter<unknown, FilterChipCollectionItem>;

	public buildCollection(extraItems: FilterChipCollectionItem[]) {
		const chip = this.chip();
		const items: FilterChipCollectionItem[] = [];

		if (chip?.anyItem) {
			items.push({
				label: this._T('ui.filter-chips.any'),
				displayLabel: '',
				value: null,
			});
		}

		items.push(...extraItems);

		this.collection.set(items.map(item => ({
			...item,
			displayLabel: isUndefined(item.displayLabel) ? item.label : item.displayLabel
		})));
	}

	public compareValues(v: unknown, def: unknown): boolean {
		return compareChipsValues(v, def);
	}

	public initConverter() {
		this.converter = {
			convert: (value: unknown) => this.collection().find(item => item.value === value) ?? { label: '', displayLabel: '', value: null },
			convertBack: (item: FilterChipCollectionItem) => item.value
		};
	}

	public isSelected(item: FilterChipCollectionItem) {
		return this.value() === item.value;
	}

	public setInitialChipValue(val: unknown) {
		const chip = this.chip();
		if (chip && !isUndefined(chip.initialChipValue)) return;
		if (chip) chip.initialChipValue = val;
	}
}
